import {_d, getAnyValue, isNullOrUndefined} from "./misc";
import {nsFa} from "../component/fa/fn";
import {IImportHrFile, TBaseOrObservableOrFunctionType, TBaseOrObservableType} from "../index";
import {storeObservable} from "./sessionstore";
import {moment} from 'moment';
import i = nsFa.i;

export function getRelativeTime(start: string, end: string): number {
    let n = moment();
    let s = convertFromServerString(start);
    let e = convertFromServerString(end);

    let isFuture = n.diff(s) < 0;
    if (isFuture) {
        return 1;
    }

    let isPast = n.diff(e) > 0;
    if (isPast) {
        return -1;
    }

    return 0;
}

export function getTimeStatusIcon(relativeTime: number, title: string): string {
    switch (relativeTime) {
        case -1:
            return `<i class="clock clock--past" title="${title}"></i>`;
        case 1:
            return `<i class="clock clock--future" title="${title}"></i>`;
        case 0:
            return `<i class="clock clock--present fa fa-clock-o" title="${title}"></i>`;
    }
    return "";
}

export function getTimeStatusText(relativeTime: number): string {
    switch (relativeTime) {
        case -1:
            return "completed";
        case 1:
            return "not started";
        default:
            return "ongoing";
    }
}

// converts a date to server friendly format
export function convertToServerString(m: moment.Moment, addSeconds = false): string | null {
    if (m.isValid()) {
        let ret = m.format("DD.MM.YYYY HH:mm");
        if (addSeconds) {
            ret += ':00';
        }
        return ret;
    } else {
        return null;
    }
}

const ServerDateFormats: string[] = [
    'DD.MM.YYYY HH:mm A', // API weirdness
    'DD.MM.YYYY HH:mm:ss A', // API weirdness
    'DD.MM.YYYY hh:mm A', // API weirdness
    'DD.MM.YYYY HH:mm',
    'DD.MM.YYYY HH:mm:ss',
    'DD.MM.YYYY hh:mm',
    'DD.MM.YYYY',
    'M/D/YYYY h:mm:ss A',
    'MM/DD/YYYY hh:mm:ss A',
    'MM/DD/YYYY hh:mm:ss A',
    'YYYY-MM-DD',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss.SSSSSSSSS',
].reduce((arr: string[], str: string) => arr.concat([str, str + 'Z']), []);

// converts a server date string to moment date
export function convertFromServerString(str: string): moment.Moment {
    storeObservable('language')() // trigger ko dependencies
    const m: moment.Moment = moment(str, ServerDateFormats);
    if (!isNullOrUndefined(str)) {
        _d.assert(m.isValid(), m)
    }
    if (m.isValid()) {
        // jolly good
        return m;
    }

    _d.warn("Date string doesn't conform to any known date format. Using new Date() instead.", str);
    return moment(new Date(str));
}

export function validateServerDate(str: string): boolean {
    const m = moment(str, ServerDateFormats, true);
    return m.isValid();
}

export function getPrettyDate(str: string, full: boolean) {
    // fa-rw: no fixed icon width — gap comes from .pretty-datetime__date flex
    return `<span class="pretty-datetime__date">${i("fa-calendar-day fa-rw")}<span class="pretty-datetime__value">${getDate(str, full)}</span></span>`;
}

/** Pretty clock + time. `showSeconds` defaults false (`LT`); true uses `LTS`. */
export function getPrettyTime(str: string, showSeconds = false) {
    return `<span class="pretty-datetime__time faded">${i("fa-clock fa-rw")}<span class="pretty-datetime__value">${getTime(str, showSeconds)}</span></span>`;
}

export function getDateTime(str: string, full: boolean): string {
    return `${getDate(str, full)} ${getTime(str, full)}`
}

export function getDate(str: string, full: boolean): string {
    let m = convertFromServerString(str);
    if (full) {
        return m.format('L');
    } else {
        const longFormat = moment.localeData().longDateFormat('L')
        const shortFormat = longFormat.replace(/(.YYYY)/gm, '');
        return m.format(shortFormat);
    }
}

export function getTime(str: string, full: boolean) {
    let m = convertFromServerString(str);
    return m.format(full ? 'LTS': 'LT');
}

export const getHrFileDate = (obj: IImportHrFile): string => {
    return obj.started;
}

/**
 * Pretty date + optional time + optional relative.
 * - `full` — date with year (default true); does not control seconds
 * - `showSeconds` — include seconds in time (default false)
 */
export function getPrettyDateTime (date: TBaseOrObservableOrFunctionType<string>, options?: {
    full?: boolean
    showTime?: boolean
    fromNow?: boolean
    showSeconds?: boolean
} | null): string {
    const val = getAnyValue(date)
    const full = options?.full ?? true
    const showTime = options?.showTime ?? true
    const fromNow = options?.fromNow ?? true
    const showSeconds = options?.showSeconds ?? false

    if (!val) {
        return ''
    }
    let chips = getPrettyDate(val, full)
    if (showTime) {
        chips += getPrettyTime(val, showSeconds)
    }
    if (fromNow ?? true) {
        const m = convertFromServerString(val)
        chips += `<span class="pretty-datetime__ago relative-time faded fnt--sizeSmall">${m.fromNow()}</span>`
    }
    // Outer clip + inner chips: leading · on a wrapped line is hidden (see _pretty-datetime.scss).
    return `<span class="pretty-datetime"><span class="pretty-datetime__chips">${chips}</span></span>`
}
